import React, { useEffect, useState } from "react";
import axios from "axios";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import { CanvasNode, CanvasNodeData } from "../../models/CanvasNode.ts";
import VariablePicker from "../Common/VariablePicker.tsx";
import AttributePicker from "../Common/AttributePicker.tsx";
import { Environment as environment } from "../../environment.ts";
import { Type, FileText, ChevronDown, Info, Plus, Trash2, Settings2, Database, LayoutList } from "lucide-react";

interface Attribute {
    attributeId: string;
    attributeName: string;
    attributeType: "TEXT" | "NUMBER" | "ARRAY" | "OBJECT" | "BOOLEAN";
}

interface ListItem {
    id: string;
    title: string;
    description: string;
}

interface ListSection {
    id: string;
    sectionTitle: string;
    items: ListItem[];
}

const MAX_ROWS = 10;

const ListDialog: React.FC<any> = ({ data, onClose }) => {
    const { nodes, setNodes, businessId } = useFlowStore();

    const [attributes, setAttributes] = useState<Attribute[]>([]);
    const [mode, setMode] = useState<"dynamic" | "static">("static");

    const [arrayAttribute, setArrayAttribute] = useState("");
    const [titlePath, setTitlePath] = useState("");
    const [descPath, setDescPath] = useState("");
    const [sampleJson, setSampleJson] = useState("");
    const [inferredPaths, setInferredPaths] = useState<string[]>([]);
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

    const [sections, setSections] = useState<ListSection[]>([]);
    const [header, setHeader] = useState("");
    const [body, setBody] = useState("");
    const [footer, setFooter] = useState("");
    const [buttonText, setButtonText] = useState("");
    const [saveAs, setSaveAs] = useState("");

    const totalItems = sections.reduce((sum, sec) => sum + (sec.items?.length ?? 0), 0);

    const flattenObject = (obj: any, prefix = ""): string[] => {
        let paths: string[] = [];
        if (typeof obj === "object" && obj !== null) {
            for (const key in obj) {
                const newPrefix = prefix ? `${prefix}.${key}` : key;
                paths.push(newPrefix);
                if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
                    paths = paths.concat(flattenObject(obj[key], newPrefix));
                }
            }
        }
        return paths;
    };

    useEffect(() => {
        if (sampleJson) {
            try {
                const parsed = JSON.parse(sampleJson);
                setInferredPaths(flattenObject(Array.isArray(parsed) ? parsed[0] : parsed));
            } catch {
                setInferredPaths([]);
            }
        } else {
            setInferredPaths([]);
        }
    }, [sampleJson]);

    useEffect(() => {
        if (!businessId) return;
        axios
            .put(`${environment.baseUrl}/fetch/attribute`, {
                businessId,
                page: 0,
                size: 100,
                templateStatus: null,
                searchText: "",
            })
            .then((r) => setAttributes(r.data.data.data || []))
            .catch(() => {});
    }, [businessId]);

    useEffect(() => {
        const node = nodes.find((n) => n.data.id === data.nodeId);
        if (!node) return;
        const content = (node.data as CanvasNodeData).content.find((c) => c.id === data.subNodeId);
        if (!content?.data) return;

        const d = content.data as any;
        const hasSections = Array.isArray(d.sections) && d.sections.length > 0;
        setMode(d.mode || (d.arrayAttribute ? "dynamic" : "static"));
        setArrayAttribute(d.arrayAttribute || "");
        setTitlePath(d.titlePath || "");
        setDescPath(d.descPath || "");
        setSections(
            hasSections
                ? d.sections
                : [{ id: crypto.randomUUID(), sectionTitle: "", items: [{ id: crypto.randomUUID(), title: "", description: "" }] }]
        );
        setHeader(d.header || "");
        setBody(d.body || "");
        setFooter(d.footer || "");
        setButtonText(d.buttonText || "");
        setSaveAs(d.saveAs || "");
        setSampleJson(d.sampleJson || "");
    }, []);

    const addSection = () => {
        if (totalItems >= MAX_ROWS) return;
        setSections((prev) => [
            ...prev,
            {
                id: crypto.randomUUID(),
                sectionTitle: "",
                items: [{ id: crypto.randomUUID(), title: "", description: "" }],
            },
        ]);
    };

    const removeSection = (sectionId: string) => {
        setSections((prev) => (prev.length <= 1 ? prev : prev.filter((s) => s.id !== sectionId)));
    };

    const addSectionItem = (secIdx: number) => {
        if (totalItems >= MAX_ROWS) return;
        setSections((prev) =>
            prev.map((section, idx) =>
                idx === secIdx
                    ? {
                          ...section,
                          items: [...section.items, { id: crypto.randomUUID(), title: "", description: "" }],
                      }
                    : section
            )
        );
    };

    const removeSectionItem = (secIdx: number, itemIdx: number) => {
        setSections((prev) =>
            prev.map((section, idx) =>
                idx === secIdx
                    ? {
                          ...section,
                          items: section.items.length <= 1 ? section.items : section.items.filter((_, i) => i !== itemIdx),
                      }
                    : section
            )
        );
    };

    const handleSave = () => {
        const updatedNodes = nodes.map((node) => {
            if (node.data.id !== data.nodeId) return node;
            return {
                ...node,
                data: {
                    ...node.data,
                    content: (node.data as CanvasNodeData).content.map((c) => {
                        if (c.id !== data.subNodeId) return c;
                        return {
                            ...c,
                            data: {
                                mode,
                                header,
                                body,
                                footer,
                                buttonText,
                                saveAs,
                                sampleJson,
                                ...(mode === "dynamic"
                                    ? {
                                          arrayAttribute,
                                          titlePath,
                                          descPath,
                                          sections: null,
                                      }
                                    : {
                                          sections,
                                          arrayAttribute: null,
                                          titlePath: null,
                                          descPath: null,
                                      }),
                            },
                        };
                    }),
                },
            };
        });
        setNodes(updatedNodes as CanvasNode[]);
        onClose();
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {/* Mode toggle */}
            <div className="flex bg-slate-100 p-1 rounded-lg">
                <button
                    className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-xs font-medium rounded-md transition-all ${mode === "dynamic" ? "bg-white shadow text-blue-600" : "text-slate-500 hover:text-slate-700"}`}
                    onClick={() => setMode("dynamic")}
                >
                    <Database size={14} /> Dynamic
                </button>
                <button
                    className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-xs font-medium rounded-md transition-all ${mode === "static" ? "bg-white shadow text-blue-600" : "text-slate-500 hover:text-slate-700"}`}
                    onClick={() => setMode("static")}
                >
                    <Settings2 size={14} /> Static
                </button>
            </div>

            {/* Header */}
            <div className="premium-field">
                <div className="flex justify-between items-center mb-1">
                    <label className="label-premium">Header <span style={{ color: "#ef4444" }}>*</span></label>
                    <VariablePicker onSelect={(val) => setHeader((prev) => prev + `{{${val}}}`)} />
                </div>
                <input className="premium-input" maxLength={60} placeholder="e.g. Namaste" value={header} onChange={(e) => setHeader(e.target.value)} />
            </div>

            {/* Body */}
            <div className="premium-field">
                <div className="flex justify-between items-center mb-1">
                    <label className="label-premium">Body Text <span style={{ color: "#ef4444" }}>*</span></label>
                    <VariablePicker onSelect={(val) => setBody((prev) => prev + `{{${val}}}`)} />
                </div>
                <textarea
                    className="premium-input"
                    style={{ minHeight: "60px" }}
                    maxLength={1024}
                    placeholder="Check out the menu below:"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                />
            </div>

            {/* Footer */}
            <div className="premium-field">
                <div className="flex justify-between items-center mb-1">
                    <label className="label-premium">Footer <span style={{ color: "#ef4444" }}>*</span></label>
                    <VariablePicker onSelect={(val) => setFooter((prev) => prev + `{{${val}}}`)} />
                </div>
                <input className="premium-input" maxLength={60} placeholder="e.g. We take deliveries" value={footer} onChange={(e) => setFooter(e.target.value)} />
            </div>

            {/* Button text */}
            <div className="premium-field">
                <div className="flex justify-between items-center mb-1">
                    <label className="label-premium">List Button Text <span style={{ color: "#ef4444" }}>*</span></label>
                    <VariablePicker onSelect={(val) => setButtonText((prev) => prev + `{{${val}}}`)} />
                </div>
                <input className="premium-input" maxLength={20} placeholder="e.g. Menu" value={buttonText} onChange={(e) => setButtonText(e.target.value)} />
                <div style={{ textAlign: "right", fontSize: "9px", color: "#9ca3af", marginTop: "2px" }}>{buttonText.length}/20</div>
            </div>

            {/* Save selection as */}
            <div className="premium-field">
                <label className="label-premium">Save selection as</label>
                <AttributePicker value={saveAs} onChange={setSaveAs} placeholder="— Save to attribute (optional) —" defaultType="OBJECT" />
                <div style={{ fontSize: "10px", color: "#64748b", marginTop: "4px" }}>
                    Stores the chosen item under this attribute so later steps can use it via {"{{"}name{"}}"}.
                    Leave blank to only use SELECTED_ITEM.
                </div>
            </div>

            {/* Dynamic mode */}
            {mode === "dynamic" && (
                <>
                    <div className="premium-field">
                        <label className="label-premium">List Data Source <span style={{ color: "#ef4444" }}>*</span></label>
                        <select
                            className={`premium-input ${!arrayAttribute ? "border-amber-200 bg-amber-50/10" : ""}`}
                            value={arrayAttribute}
                            onChange={(e) => setArrayAttribute(e.target.value)}
                        >
                            <option value="">Select Array Attribute</option>
                            {attributes
                                .filter((a) => a.attributeType?.toUpperCase() === "ARRAY")
                                .map((a) => (
                                    <option key={a.attributeId} value={a.attributeName}>
                                        {a.attributeName}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div style={{ borderTop: "1px solid #f1f5f9" }} />

                    <div className="premium-field">
                        <label className="label-premium" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <Info size={12} style={{ color: "#3b82f6" }} /> Data Mapping Assistant
                        </label>
                        <textarea
                            className="premium-input"
                            style={{ fontFamily: "monospace", fontSize: "10px", background: "#f8fafc", borderStyle: "dashed", marginTop: "6px" }}
                            placeholder='Sample item: { "name": "Paneer Tikka", "desc": "Grilled paneer" }'
                            value={sampleJson}
                            onChange={(e) => setSampleJson(e.target.value)}
                        />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                        <div className="premium-field" style={{ position: "relative" }}>
                            <label className="label-premium" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <Type size={12} /> Title Path <span style={{ color: "#ef4444" }}>*</span>
                            </label>
                            <div style={{ position: "relative" }}>
                                <input className="premium-input" style={{ paddingRight: "28px" }} placeholder="e.g. name" value={titlePath} onChange={(e) => setTitlePath(e.target.value)} />
                                {inferredPaths.length > 0 && (
                                    <button
                                        style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}
                                        onClick={() => setActiveDropdown(activeDropdown === "title" ? null : "title")}
                                    >
                                        <ChevronDown size={14} />
                                    </button>
                                )}
                            </div>
                            {activeDropdown === "title" && (
                                <div className="path-menu shadow-xl border border-slate-200">
                                    {inferredPaths.map((p) => (
                                        <div key={p} className="path-menu-item" onClick={() => { setTitlePath(p); setActiveDropdown(null); }}>
                                            {p}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="premium-field" style={{ position: "relative" }}>
                            <label className="label-premium" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <FileText size={12} /> Description Path
                            </label>
                            <div style={{ position: "relative" }}>
                                <input className="premium-input" style={{ paddingRight: "28px" }} placeholder="e.g. description" value={descPath} onChange={(e) => setDescPath(e.target.value)} />
                                {inferredPaths.length > 0 && (
                                    <button
                                        style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}
                                        onClick={() => setActiveDropdown(activeDropdown === "desc" ? null : "desc")}
                                    >
                                        <ChevronDown size={14} />
                                    </button>
                                )}
                            </div>
                            {activeDropdown === "desc" && (
                                <div className="path-menu shadow-xl border border-slate-200">
                                    {inferredPaths.map((p) => (
                                        <div key={p} className="path-menu-item" onClick={() => { setDescPath(p); setActiveDropdown(null); }}>
                                            {p}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ fontSize: "10px", color: "#64748b", background: "#f8fafc", borderRadius: "6px", padding: "8px", border: "1px solid #e2e8f0" }}>
                        Dynamic lists use one shared connection dot on the node. Up to {MAX_ROWS} rows are shown from the array attribute.
                    </div>
                </>
            )}

            {/* Static mode */}
            {mode === "static" && (
                <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", padding: "6px 8px", background: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                        <label className="label-premium" style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: "6px" }}>
                            <LayoutList size={12} /> Sections ({totalItems}/{MAX_ROWS} items)
                        </label>
                        <button className="add-section-btn" style={{ borderColor: "var(--primary-bg-color)", color: "var(--primary-bg-color)" }} onClick={addSection} disabled={totalItems >= MAX_ROWS}>
                            <Plus size={14} /> Add Section
                        </button>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {sections.map((section, secIdx) => (
                            <div key={section.id} className="section-container">
                                <button className="section-remove-btn" onClick={() => removeSection(section.id)} disabled={sections.length <= 1}>
                                    <Trash2 size={11} />
                                </button>

                                <div className="premium-field">
                                    <label className="label-premium">Section Title <span style={{ color: "#ef4444" }}>*</span></label>
                                    <input
                                        className="premium-input"
                                        maxLength={24}
                                        placeholder="e.g. Veg"
                                        value={section.sectionTitle}
                                        onChange={(e) =>
                                            setSections((prev) =>
                                                prev.map((s, idx) => (idx === secIdx ? { ...s, sectionTitle: e.target.value } : s))
                                            )
                                        }
                                    />
                                </div>

                                {section.items.map((item, itemIdx) => (
                                    <div key={item.id} className="item-container">
                                        <button className="item-remove-btn" onClick={() => removeSectionItem(secIdx, itemIdx)} disabled={section.items.length <= 1}>
                                            <Trash2 size={10} />
                                        </button>
                                        <input
                                            className="premium-input"
                                            maxLength={24}
                                            placeholder="Title"
                                            value={item.title}
                                            onChange={(e) =>
                                                setSections((prev) =>
                                                    prev.map((s, sIdx) =>
                                                        sIdx === secIdx
                                                            ? {
                                                                  ...s,
                                                                  items: s.items.map((it, i) => (i === itemIdx ? { ...it, title: e.target.value } : it)),
                                                              }
                                                            : s
                                                    )
                                                )
                                            }
                                        />
                                        <input
                                            className="premium-input"
                                            style={{ marginTop: "4px" }}
                                            maxLength={72}
                                            placeholder="Description"
                                            value={item.description}
                                            onChange={(e) =>
                                                setSections((prev) =>
                                                    prev.map((s, sIdx) =>
                                                        sIdx === secIdx
                                                            ? {
                                                                  ...s,
                                                                  items: s.items.map((it, i) => (i === itemIdx ? { ...it, description: e.target.value } : it)),
                                                              }
                                                            : s
                                                    )
                                                )
                                            }
                                        />
                                    </div>
                                ))}

                                <button
                                    className="add-item-btn"
                                    onClick={() => addSectionItem(secIdx)}
                                    disabled={totalItems >= MAX_ROWS}
                                >
                                    + Add Item
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", paddingTop: "8px", borderTop: "1px solid #f1f5f9" }}>
                <button onClick={onClose} className="dialog-btn-secondary">
                    Cancel
                </button>
                <button onClick={handleSave} className="dialog-btn-primary">
                    Save
                </button>
            </div>

            <style>{`
                .label-premium {
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                    color: #64748b;
                    font-weight: 700;
                    margin-bottom: 2px;
                }
                .premium-field { display: flex; flex-direction: column; }
                .premium-input {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 4px;
                    padding: 6px 8px;
                    font-size: 11px;
                    color: #1e293b;
                    width: 100%;
                    box-sizing: border-box;
                }
                .premium-input:focus {
                    border-color: var(--primary-bg-color);
                    box-shadow: 0 0 0 2px rgba(10,71,76,0.1);
                    outline: none;
                }
                .add-section-btn, .add-item-btn {
                    background: #ffffff;
                    font-size: 10px;
                    padding: 4px 10px;
                    border-radius: 4px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    border: 1px solid;
                    cursor: pointer;
                }
                .add-section-btn:disabled, .add-item-btn:disabled { opacity: 0.5; cursor: not-allowed; }
                .add-item-btn {
                    margin-top: 6px;
                    color: #3b82f6;
                    border-color: #bfdbfe;
                    width: fit-content;
                }
                .section-container {
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    padding: 10px;
                    background: #f8fafc;
                    position: relative;
                }
                .section-remove-btn, .item-remove-btn {
                    position: absolute;
                    top: 6px;
                    right: 6px;
                    background: #fee2e2;
                    color: #ef4444;
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid #fecaca;
                    cursor: pointer;
                    padding: 0;
                }
                .section-remove-btn:disabled, .item-remove-btn:disabled { opacity: 0.35; cursor: not-allowed; }
                .item-container {
                    position: relative;
                    border: 1px dashed #fca5a5;
                    border-radius: 4px;
                    padding: 8px 28px 8px 8px;
                    margin-top: 6px;
                    background: white;
                }
                .item-remove-btn { top: 4px; right: 4px; width: 18px; height: 18px; }
                .path-menu {
                    position: absolute; top: 100%; left: 0; right: 0;
                    background: white; border-radius: 6px; z-index: 100;
                    max-height: 120px; overflow-y: auto; margin-top: 2px;
                }
                .path-menu-item {
                    padding: 6px 10px; font-size: 10px; color: #475569;
                    cursor: pointer; border-bottom: 1px solid #f1f5f9;
                }
                .path-menu-item:hover { background: #f8fafc; color: #0ea5e9; }
                .dialog-btn-secondary {
                    padding: 8px 20px; font-size: 13px; border-radius: 8px;
                    border: 1px solid #e2e8f0; background: white; cursor: pointer; color: #64748b;
                }
                .dialog-btn-primary {
                    padding: 8px 24px; font-size: 13px; border-radius: 8px;
                    border: none; background: var(--primary-bg-color); color: white;
                    cursor: pointer; font-weight: 600;
                }
            `}</style>
        </div>
    );
};

export default ListDialog;
